import { Component, EventEmitter, Input, OnInit, Output, inject } from "@angular/core";
import { CommonModule } from "@angular/common";
import { MatIconModule } from "@angular/material/icon";
import { MatTabsModule } from "@angular/material/tabs";
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from "@angular/material/dialog";
import { MatButtonModule } from "@angular/material/button";
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { IncidentDocumentsService } from "@app/incident/services/incident-documents.service";
import { IncidentHyperlinkModel } from "../models/hyperlink-model";
import { BaseDocumentsService } from "@app/shared/cerrix-documents/services/base-documents.service";
import { EditHyperlinkPopup } from "../models/edit-hyperlink-popup.model";
import { CerrixButtonComponent } from "@app/incident/components/src/lib/core/cerrix-button/cerrix-button.component";
import { CerrixTitleComponent } from "@app/incident/components/src/lib/core/cerrix-title/cerrix-title.component";
import { CerrixFieldWrapperComponent } from "@app/incident/components/src/lib/core/cerrix-field-wrapper/cerrix-field-wrapper.component";
import { CerrixSelectComponent } from "@app/incident/components/src/lib/core/cerrix-select/cerrix-select.component";
import { CerrixTextareaComponent } from "@app/incident/components/src/lib/core/cerrix-textarea/cerrix-textarea.component";
import { CerrixInputComponent } from "@app/incident/components/src/lib/core/cerrix-input/cerrix-input.component";
import { SelectOptionModel } from "@app/incident/components/src/lib/core/models/select-option.model";
import { CerrixValidators } from "@app/incident/components/src/lib/core/validation/CerrixValidators";

@Component({
    selector: "cerrix-hyperlinks-edit",
    templateUrl: "./cerrix-hyperlinks-edit.component.html",
    styleUrls: ["./cerrix-hyperlinks-edit.component.scss"],
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        MatIconModule,
        MatButtonModule,
        MatDialogModule,
        MatTabsModule,
        CerrixButtonComponent,
        CerrixTitleComponent,
        CerrixFieldWrapperComponent,
        CerrixSelectComponent,
        CerrixTextareaComponent,
        CerrixInputComponent,
    ],
    providers: [{ provide: BaseDocumentsService, useClass: IncidentDocumentsService }]
})
export class CerrixEditHyperlinkComponent implements OnInit {
    private dialogRef: MatDialogRef<EditHyperlinkPopup, IncidentHyperlinkModel[]> =
        inject(MatDialogRef);
    @Input() public dialogData: EditHyperlinkPopup = inject(MAT_DIALOG_DATA);
    @Output() public emitDownload = new EventEmitter<IncidentHyperlinkModel>();

    protected hyperlinkTypes: SelectOptionModel[];

    protected dialogTitle = "";
    protected btnLabel = "";

    private readonly maxTextareaLength = 32767;
    private readonly maxTextfieldLength = 300;

    protected hyperlinkForm: FormGroup = new FormGroup({
        hyperlinkTypeId: new FormControl(null),
        description: new FormControl(null, [
            CerrixValidators.maxLength(this.maxTextareaLength, "Description exceeds max length"),
        ]),
        name: new FormControl(null, [
            CerrixValidators.required("Name is required"),
            CerrixValidators.maxLength(this.maxTextfieldLength, "Name exceeds max length"),
        ]),
        hyperlink: new FormControl(null, [
            CerrixValidators.required("Hyperlink is required"),
            CerrixValidators.validateWebsite("Hyperlink", "Hyperlink is not valid"),
        ]),
    });

    protected historyLoaded: boolean = false;
    protected historyDocuments: IncidentHyperlinkModel[] = [];

    ngOnInit(): void {
        if (this.dialogData.openReason === "Add") {
            this.dialogTitle = "Add hyperlink";
            this.btnLabel = "Add hyperlink";
        } else if (this.dialogData.openReason === "Edit") {
            this.dialogTitle = "Edit hyperlink";
            this.btnLabel = "Apply changes";

            this.hyperlinkForm = new FormGroup({
                hyperlinkTypeId: new FormControl(this.dialogData.hyperlinks[0].hyperlinkTypeId),
                description: new FormControl(this.dialogData.hyperlinks[0].description, [
                    CerrixValidators.maxLength(
                        this.maxTextareaLength,
                        "Description exceeds max length"
                    ),
                ]),
                name: new FormControl(this.dialogData.hyperlinks[0].name, [
                    CerrixValidators.required("Name is required"),
                    CerrixValidators.maxLength(this.maxTextfieldLength, "Name exceeds max length"),
                ]),
                hyperlink: new FormControl(this.dialogData.hyperlinks[0].hyperlink, [
                    CerrixValidators.required("Hyperlink is required"),
                    CerrixValidators.validateWebsite("Hyperlink", "Hyperlink is not valid"),
                ]),
            });
        } else {
            this.dialogTitle = "View hyperlink";

            this.hyperlinkForm = new FormGroup({
                hyperlinkTypeId: new FormControl({
                    value: this.dialogData.hyperlinks[0].hyperlinkTypeId,
                    disabled: true,
                }),
                description: new FormControl({
                    value: this.dialogData.hyperlinks[0].description,
                    disabled: true,
                }),
                name: new FormControl({
                    value: this.dialogData.hyperlinks[0].name,
                    disabled: true,
                }),
                hyperlink: new FormControl({
                    value: this.dialogData.hyperlinks[0].hyperlink,
                    disabled: true,
                }),
            });
        }
    }

    protected onApplyChanges() {
        this.hyperlinkForm.markAllAsTouched();
        this.hyperlinkForm.updateValueAndValidity();
        if (this.hyperlinkForm.valid) {
            if (this.dialogData.openReason === "Add") {
                this.dialogData.hyperlinks.push({
                    hyperlinkTypeId: this.hyperlinkForm.value.hyperlinkTypeId,
                    description: this.hyperlinkForm.value.description,
                    name: this.hyperlinkForm.value.name,
                    hyperlink: this.hyperlinkForm.value.hyperlink,
                    isNew: true,
                });
            } else if (this.dialogData.openReason === "Edit") {
                this.dialogData.hyperlinks = this.dialogData.hyperlinks.map((d) => {
                    d.hyperlinkTypeId = this.hyperlinkForm.value.hyperlinkTypeId;
                    d.description = this.hyperlinkForm.value.description;
                    d.name = this.hyperlinkForm.value.name;
                    d.hyperlink = this.hyperlinkForm.value.hyperlink;
                    return d;
                });
            }
            this.dialogData.hyperlinks.forEach((hyperlink) => {
                hyperlink.hyperlinkType = this.dialogData.hyperlinkTypes.find(
                    (type) => type.id === hyperlink.hyperlinkTypeId
                )?.name;
            });

            this.dialogRef.close(this.dialogData.hyperlinks);
        }
    }
}
